import React from 'react';
import { CaseGenerationLoading } from './CaseGenerationLoading';

/**
 * GameSessionGate - renders the pre-play surfaces of a game session
 * (generation/recovery takeover, restore spinner, or nothing at all) and only
 * hands off to `children` once the session is playable.
 *
 * Shared by /game and the /game3d view so both gate identically.
 * `session` is the object returned by `useGameSession()`.
 */
export const GameSessionGate = ({ session, children }) => {
  const {
    selectedTheme,
    selectedDifficulty,
    theme,
    isFeaturedSession,
    isMalformedFeaturedSession,
    loading,
    restoring,
    generationProgress,
    generationRecovery,
    handleRetryGeneration,
    handleChangeDifficulty,
    handleViewArchive,
  } = session;

  // A corrupted featured id renders nothing: the hook has already cleared it
  // and sent the player home.
  if (isMalformedFeaturedSession) {
    return null;
  }

  // A featured session is exempt: its theme arrives with the compat `load`,
  // and the restore spinner below is what should cover that wait.
  if (!selectedTheme && !generationRecovery && !isFeaturedSession) {
    return null;
  }

  if (loading) {
    return (
      <CaseGenerationLoading
        theme={selectedTheme || 'noir'}
        difficulty={selectedDifficulty || 'medium'}
        progress={generationProgress}
        isFailed={Boolean(generationRecovery)}
        errorMessage={generationRecovery?.message}
        jobId={generationRecovery?.referenceId || generationRecovery?.jobId}
        retryable={generationRecovery?.retryable}
        lifecycleStatus={generationRecovery?.kind}
        onRetry={handleRetryGeneration}
        onChangeDifficulty={handleChangeDifficulty}
        onViewArchive={handleViewArchive}
      />
    );
  }

  if (restoring) {
    return (
      <div
        data-testid="session-restoring"
        className={`min-h-screen bg-gradient-to-br ${theme.bg} flex flex-col items-center justify-center gap-4`}
      >
        <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white/80 animate-spin" />
        <p className="text-white/70 text-sm tracking-wide">Restoring your investigation...</p>
      </div>
    );
  }

  return children;
};

export default GameSessionGate;
